import { useMemo } from 'react';

/**
 * Profile photograph, with a deterministic generated fallback.
 *
 * The fallback is derived purely from the employee id, so the same person gets
 * the same colours on every machine with no network request and no third-party
 * avatar service — a directory of staff should not leak who works here to an
 * external host. (The original portal did the same thing; this is a
 * reimplementation of that idea, not a byte-for-byte copy of its SVG.)
 */
const PALETTE = [
  ['#10243f', '#2c4a72'],
  ['#1e5245', '#2f7d69'],
  ['#5a2c4a', '#87466f'],
  ['#8c6f28', '#c8a24a'],
  ['#33405c', '#55688f'],
  ['#6b3320', '#a15436'],
  ['#264653', '#3c7080'],
  ['#4a3b6b', '#71609b'],
] as const;

function hashOf(value: string): number {
  let hash = 0;
  for (let i = 0; i < value.length; i += 1) {
    hash = (hash << 5) - hash + value.charCodeAt(i);
    hash |= 0;
  }
  return Math.abs(hash);
}

interface Props {
  employeeId: string;
  fullName: string;
  /** Legacy data URI from a migrated record, if any. */
  photoUrl?: string | null;
  /** True when the server holds a photograph this viewer may see. */
  hasPhoto?: boolean;
  size?: number;
  className?: string;
}

export function Avatar({ employeeId, fullName, photoUrl, hasPhoto, size = 44, className }: Props) {
  const initials = useMemo(() => {
    const parts = fullName.trim().split(/\s+/).filter(Boolean);
    if (parts.length === 0) return '?';
    if (parts.length === 1) return parts[0]!.slice(0, 2).toUpperCase();
    return (parts[0]![0]! + parts[parts.length - 1]![0]!).toUpperCase();
  }, [fullName]);

  const [dark, light] = PALETTE[hashOf(employeeId) % PALETTE.length]!;

  // A stored photograph wins; then a migrated data URI; then the fallback.
  if (hasPhoto) {
    return (
      <img
        className={className ?? 'avatar'}
        src={`/api/v1/employees/${employeeId}/photo`}
        alt=""
        width={size}
        height={size}
        loading="lazy"
      />
    );
  }

  if (photoUrl) {
    return <img className={className ?? 'avatar'} src={photoUrl} alt="" width={size} height={size} />;
  }

  const gradientId = `av-${employeeId}`;
  return (
    <svg
      className={className ?? 'avatar'}
      width={size}
      height={size}
      viewBox="0 0 100 100"
      role="img"
      aria-label=""
      focusable="false"
    >
      <defs>
        <linearGradient id={gradientId} x1="0" y1="0" x2="1" y2="1">
          <stop offset="0%" stopColor={light} />
          <stop offset="100%" stopColor={dark} />
        </linearGradient>
      </defs>
      <circle cx="50" cy="50" r="50" fill={`url(#${gradientId})`} />
      <text
        x="50"
        y="50"
        textAnchor="middle"
        dominantBaseline="central"
        fill="#fff"
        fontSize="38"
        fontWeight="600"
        fontFamily="system-ui, sans-serif"
      >
        {initials}
      </text>
    </svg>
  );
}
